/*
 * As telas de lista do chat interno do tema (features/chat), refeitas sobre o
 * estado do ramon/chat: a barra de cima (c-navbar), as listas de canais e de
 * MDs (chat-channel-row), o "x" que fecha a MD e as abas de baixo (c-footer:
 * Canais, MDs, Meus tópicos, Pesquisar). As classes são as
 * mesmas do features/chat — o Chat.less também compila com o ramon/chat —,
 * então as telas saem idênticas às do chat do tema.
 *
 * A navegação é a do ramon/chat: na página cheia, as rotas dele (com
 * `?tab=channels|dms` na lista); na gaveta, uma aba guardada aqui.
 */
import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import Icon from 'flarum/common/components/Icon';
import Tooltip from 'flarum/common/components/Tooltip';
import classList from 'flarum/common/utils/classList';
import { t } from '../../utils/settings';
import { formatWith } from '../../utils/dates';
import { canUseChat, channelUrl, chatState, leaveDirect, openChannel, ramonChannelIcon } from './bridge';
import { safely, sidebarHooks } from './hooks';
/** Tradução do chat do tema (`ramon-canela.forum.chat.*`): os rótulos são os mesmos das telas internas. */
export function ct(key, params = {}) {
  return t(`chat.${key}`, params);
}
function startOfDay(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}
function shortTime(date) {
  const time = formatWith(date, {
    hour: '2-digit',
    minute: '2-digit'
  });
  return startOfDay(date) === startOfDay(new Date()) ? time : formatWith(date, {
    day: 'numeric',
    month: 'short'
  }).replace(/\./g, '').replace(/\sde\s/g, ' ');
}

/** O ponto (há não lidas) ou o número verde (menções e MDs), como o UnreadIndicator interno. */
export function UnreadIndicator(urgent, unread, className = 'chat-channel-unread-indicator') {
  if (urgent > 0) {
    return m("div", {
      className: `${className} -urgent`
    }, m("div", {
      className: `${className}__number`
    }, urgent > 99 ? '99+' : urgent));
  }
  return unread ? m("div", {
    className: className
  }) : null;
}
/*
 * Os tooltips daqui moram no <body> (`container`): dentro da gaveta, que corta
 * o que passa da borda (overflow: hidden), eles ficavam pela metade e por
 * baixo da barra dela. Na gaveta, os da direita abrem para a esquerda, para
 * dentro da tela.
 */

/** A barra de cima (c-navbar): voltar, o título e os botões. */
export function Navbar(attrs) {
  return m("div", {
    className: classList('c-navbar-container', {
      '-clickable': !!attrs.onclick
    }),
    onclick: attrs.onclick
  }, m("nav", {
    className: "c-navbar"
  }, attrs.back && m("button", {
    type: "button",
    className: "Button Button--icon Button--flat c-navbar__back-button",
    "aria-label": ct('channel_info.back_to_channel'),
    title: ct('channel_info.back_to_channel'),
    onclick: e => {
      e.stopPropagation();
      attrs.back();
    }
  }, m(Icon, {
    name: "fas fa-chevron-left"
  })), m("div", {
    className: "c-navbar__title"
  }, m("span", {
    className: "c-navbar__title-text"
  }, attrs.title)), m("ul", {
    className: "c-navbar__actions"
  }, (attrs.actions || []).map(action => m("li", {
    key: action.key
  }, m(Tooltip, {
    text: action.title,
    container: "body"
  }, m("button", {
    type: "button",
    className: classList('Button Button--icon Button--flat c-navbar__button', action.className),
    "aria-label": action.title,
    onclick: e => {
      e.stopPropagation();
      action.onclick(e);
    }
  }, m(Icon, {
    name: action.icon
  }))))))));
}

/** O endereço de uma aba na página cheia. */
export function tabUrl(tab) {
  if (tab === 'threads') return app.route('chat.threads');
  if (tab === 'search') return app.route('chat.search');
  return `${app.route('chat.index')}?tab=${tab}`;
}

/** As abas na página: a lista, MDs, Meus tópicos e Pesquisar. */
export function currentTab() {
  const route = String(app.current.get('routeName') || '');
  if (route === 'chat.threads') return 'threads';
  if (route === 'chat.search') return 'search';
  if (route !== 'chat.index') return null;
  return m.route.param('tab') === 'dms' ? 'dms' : 'channels';
}
function phone() {
  return window.innerWidth < 768;
}

/** Os títulos das listas (os do chat do tema). */
export function tabTitle(tab, inDrawer = false) {
  if (inDrawer) return ct('heading');
  return {
    channels: ct('channel_list.title'),
    dms: ct('direct_messages.title'),
    threads: ct('my_threads.title'),
    search: ct('search.title')
  }[tab];
}
/** As abas de baixo (c-footer), com o ponto/número de cada uma. */
export class ChatFooter extends Component {
  view() {
    const {
      current,
      onselect
    } = this.attrs;
    const state = chatState();
    const channels = state?.categoryChannels() || [];
    const direct = state?.directChannels() || [];
    const publicUrgent = channels.reduce((n, c) => n + (c.unreadMentionsCount() ?? 0), 0);
    const publicUnread = channels.some(c => c.hasUnread());
    const dmUrgent = direct.reduce((n, c) => n + (c.isMuted() ? 0 : c.unreadCount() ?? 0), 0);
    const tab = (name, icon, label, indicator) => m("button", {
      type: "button",
      className: classList('Button Button--flat c-footer__item', {
        '--active': current === name
      }),
      id: `c-footer-${name === 'dms' ? 'direct-messages' : name}`,
      "aria-label": label,
      onclick: () => {
        if (onselect && (name === 'channels' || name === 'dms')) onselect(name);else m.route.set(tabUrl(name));
      }
    }, m(Icon, {
      name: icon
    }), m("span", {
      className: "c-footer__label"
    }, label), indicator);
    return m("nav", {
      className: "c-footer"
    }, tab('channels', 'fas fa-comments', ct('channel_list.title'), UnreadIndicator(publicUrgent, publicUnread, 'c-unread-indicator')), canDirectMessage() && tab('dms', 'fas fa-users', ct('direct_messages.title'), UnreadIndicator(dmUrgent, false, 'c-unread-indicator')), tab('threads', 'fas fa-list-ul', ct('my_threads.title'), null), tab('search', 'fas fa-magnifying-glass', ct('search.short_title'), null));
  }
}
function canDirectMessage() {
  return !!app.forum.attribute('canStartChatDirect') || !!chatState()?.directChannels().length;
}
function sorted(channels, section) {
  const sort = sidebarHooks.sortChannels;
  return sort ? safely(() => sort(channels.slice(0), section), channels) : channels;
}

/** "Nova mensagem": o gancho dos recursos (features/newMessage), quando existe. */
function newMessage() {
  const open = sidebarHooks.newMessage;
  return open && app.forum.attribute('canStartChatDirect') ? open : null;
}
/** A linha do canal (chat-channel-row), com o "x" de fechar nas MDs. */
export class ChannelRow extends Component {
  leaving = false;
  view() {
    const {
      channel,
      inDrawer,
      leave,
      onopen
    } = this.attrs;
    const id = Number(channel.id());
    const direct = channel.isDirect();
    const mentions = channel.unreadMentionsCount() ?? 0;
    const unread = channel.hasUnread();
    const urgent = direct ? channel.isMuted() ? 0 : channel.unreadCount() ?? 0 : mentions;
    const active = !inDrawer && chatState()?.activeChannelId === id && String(app.current.get('routeName') || '').startsWith('chat.channel');
    const last = channel.lastMessage?.();
    const lastAt = channel.lastMessageAt?.() || null;
    return m("a", {
      className: classList('chat-channel-row', {
        active,
        muted: channel.isMuted(),
        'has-unread': unread || urgent > 0,
        'can-leave': leave && !phone(),
        '-fade-out': this.leaving
      }),
      href: channelUrl(id),
      "data-chat-channel-id": id,
      onclick: e => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
        e.preventDefault();
        if (onopen) onopen(channel);else openChannel(id);
      }
    }, m("div", {
      className: classList('chat-channel-row__content', direct ? 'is-dm' : 'is-category')
    }, m("div", {
      className: classList('chat-channel-icon', direct ? '--avatar' : '--emoji')
    }, ramonChannelIcon(channel, 'CanelaRamonChatListIcon')), m("div", {
      className: "chat-channel-row__info"
    }, m("div", {
      className: "chat-channel-row__name-container"
    }, m("div", {
      className: "chat-channel-name"
    }, m("span", {
      className: "chat-channel-name__label"
    }, channel.displayName())), channel.isPrivate() && !direct && m("span", {
      className: "chat-channel-row__starred-icon"
    }, m(Icon, {
      name: "fas fa-lock"
    })), sidebarHooks.channelSuffix && safely(() => sidebarHooks.channelSuffix(channel), null)), m("div", {
      className: "chat-channel__metadata"
    }, direct && lastAt && m("div", {
      className: "chat-channel__metadata-date"
    }, shortTime(lastAt)), UnreadIndicator(urgent, unread)), direct && phone() && last?.content && m("div", {
      className: "chat-channel__last-message"
    }, excerpt(last))), leave && !phone() && m("button", {
      type: "button",
      className: "Button Button--icon Button--flat chat-channel-leave-btn toggle-channel-membership-button -leave",
      title: ct('direct_messages.close'),
      "aria-label": ct('direct_messages.close'),
      onclick: e => {
        e.preventDefault();
        e.stopPropagation();
        this.leave();
      }
    }, m(Icon, {
      name: "fas fa-xmark"
    }))));
  }

  /** O "x" da MD: some da lista com uma animação e sai da conversa. */
  leave() {
    this.leaving = true;
    leaveDirect(this.attrs.channel).then(left => {
      if (!left) this.leaving = false;
      m.redraw();
    });
  }
}

/** O começo do texto da última mensagem, sem as marcações. */
function excerpt(message) {
  const html = String(message.contentHtml?.() || message.content?.() || '');
  const div = document.createElement('div');
  div.innerHTML = html;
  return (div.textContent || '').trim().slice(0, 120);
}
/** As listas de canais e de MDs. */
export class ChannelsList extends Component {
  view() {
    const {
      which,
      inDrawer
    } = this.attrs;
    return m("div", {
      className: classList('channels-list', {
        'public-channels': which === 'public',
        'direct-message-channels': which === 'direct'
      })
    }, which !== 'direct' && this.publicSection(inDrawer, which === 'all'), which !== 'public' && canDirectMessage() && this.directSection(inDrawer, which === 'all'));
  }
  browse() {
    if (this.attrs.onbrowse) this.attrs.onbrowse();else m.route.set(app.route('chat.browse'));
  }
  publicSection(inDrawer, withDivider) {
    const state = chatState();
    const channels = sorted(state?.categoryChannels() || [], 'channels');
    return m("div", {
      className: "channels-list-container public-channels"
    }, (withDivider || inDrawer) && m("div", {
      className: "chat-channel-divider public-channels-section"
    }, m("span", {
      className: "channel-title"
    }, ct('chat_channels')), m("div", {
      className: "chat-channel-divider__actions"
    }, m(Tooltip, {
      text: ct('browse_all_channels'),
      container: "body",
      position: inDrawer ? 'left' : 'top'
    }, m("button", {
      type: "button",
      className: "Button Button--icon Button--flat open-browse-page-btn",
      "aria-label": ct('browse_all_channels'),
      onclick: () => this.browse()
    }, m(Icon, {
      name: "fas fa-pencil"
    }))))), channels.length ? channels.map(channel => m(ChannelRow, {
      key: channel.id(),
      channel: channel,
      inDrawer: inDrawer,
      onopen: this.attrs.onopen
    })) : state?.channelsLoaded ? m("div", {
      className: "channels-list__empty"
    }, m("p", null, ct('no_public_channels')), m("button", {
      type: "button",
      className: "Button Button--primary",
      onclick: () => this.browse()
    }, ct('no_public_channels_cta'))) : null);
  }
  directSection(inDrawer, withDivider) {
    const state = chatState();
    const channels = sorted(state?.directChannels() || [], 'direct');
    const create = newMessage();
    return m("div", {
      className: "channels-list-container direct-message-channels"
    }, (withDivider || inDrawer) && m("div", {
      className: "chat-channel-divider direct-message-channels-section"
    }, m("span", {
      className: "channel-title"
    }, ct('direct_messages.title')), create && m("div", {
      className: "chat-channel-divider__actions"
    }, m(Tooltip, {
      text: ct('direct_messages.new_group'),
      container: "body",
      position: inDrawer ? 'left' : 'top'
    }, m("button", {
      type: "button",
      className: "Button Button--icon Button--flat open-draft-channel-page-btn",
      "aria-label": ct('direct_messages.new_group'),
      onclick: () => create()
    }, m(Icon, {
      name: "fas fa-plus"
    }))))), channels.length ? channels.map(channel => m(ChannelRow, {
      key: channel.id(),
      channel: channel,
      inDrawer: inDrawer,
      leave: true,
      onopen: this.attrs.onopen
    })) : state?.channelsLoaded ? m("div", {
      className: "channels-list__empty"
    }, m("p", null, ct('no_direct_message_channels')), create && m("button", {
      type: "button",
      className: "Button Button--primary",
      onclick: () => create()
    }, ct('no_direct_message_channels_cta'))) : null);
  }
}

/** "Fechar chat em tela cheia": a conversa (ou a lista) passa para a gaveta e a página volta ao fórum. */
export function toDrawer() {
  const drawer = flarum.reg.checkModule('ramon-chat', 'forum/components/ChatDrawer');
  m.route.set(app.route('index'));
  if (drawer?.open) setTimeout(() => drawer.open().catch(() => {}), 0);
}

/** O chat está disponível e a integração ligada (o resto do arquivo supõe isso). */
export function homeAvailable() {
  return canUseChat() && !!chatState();
}
/**
 * A lista da gaveta (a do chat do tema): a seção "Canais" com o lápis, ou as
 * MDs, e as abas de baixo. "Meus fios" e "Pesquisar" abrem a página cheia,
 * onde essas telas moram no ramon/chat.
 */
export class DrawerHome extends Component {
  tab = 'channels';
  view() {
    return m("div", {
      className: `c-drawer-routes c-routes --${this.tab === 'dms' ? 'direct-messages' : 'channels'} CanelaRamonChatDrawerHome`
    }, m("div", {
      className: "chat-drawer-content"
    }, m(ChannelsList, {
      which: this.tab === 'dms' ? 'direct' : 'public',
      inDrawer: true,
      onopen: this.attrs.onopen
    })), m(ChatFooter, {
      current: this.tab,
      onselect: tab => {
        this.tab = tab === 'dms' ? 'dms' : 'channels';
      }
    }));
  }
}
flarum.reg.add('ramon-canela', 'forum/thirdparty/ramonChat/ui', { ct: ct,UnreadIndicator: UnreadIndicator,Navbar: Navbar,tabUrl: tabUrl,currentTab: currentTab,tabTitle: tabTitle,ChatFooter: ChatFooter,ChannelRow: ChannelRow,ChannelsList: ChannelsList,toDrawer: toDrawer,homeAvailable: homeAvailable,DrawerHome: DrawerHome, });